import type { GameRunStats } from '../../domain/gameplay/GameRunStats';
import type { EventBus } from '../../services/EventBus';
import { createSubmitScorePayload } from './services/game-run-payload';
import type { LeaderboardRepository } from './services/leaderboard-service';
import type { LeaderboardStorage } from './services/leaderboard-storage';
import { ScoreSubmissionService } from './services/score-submission-service';
import { LeaderboardPanel } from './ui/LeaderboardPanel';
import { LeaderboardDialog } from './ui/LeaderboardDialog';

const clientVersion = import.meta.env.VITE_APP_VERSION?.trim() || '1.0.0';

export class LeaderboardController {
  private readonly panel: LeaderboardPanel;
  private readonly submission: ScoreSubmissionService;
  private readonly dialog: LeaderboardDialog;
  private readonly disposeGameOver: () => void;
  private run?: GameRunStats;

  public constructor(
    root: HTMLElement,
    private readonly events: EventBus,
    private readonly repository: LeaderboardRepository,
    private readonly storage: LeaderboardStorage,
  ) {
    this.panel = new LeaderboardPanel(root, (nickname) => void this.submit(nickname));
    this.dialog = new LeaderboardDialog(root, this.panel.boardElement);
    this.submission = new ScoreSubmissionService(repository);
    this.disposeGameOver = events.on('game:over', ({ run }) => this.open(run));
  }

  public destroy(): void {
    this.disposeGameOver();
    this.dialog.destroy();
    this.panel.destroy();
  }

  public get isDialogOpen(): boolean {
    return this.dialog.isOpen;
  }

  public openIntro(opener: HTMLElement): void {
    if (!this.dialog.open(opener)) return;
    this.panel.prepareStandaloneLeaderboard();
    this.panel.setLeaderboardLoading();
    void this.loadLeaderboard();
  }

  private open(run: GameRunStats): void {
    this.dialog.close();
    this.run = run;
    this.panel.begin(run, this.storage.readLastNickname());
    this.panel.setLeaderboardLoading();
    void this.loadLeaderboard(run.sessionId);
  }

  private async loadLeaderboard(sessionId?: string): Promise<void> {
    try {
      const leaderboard = await this.repository.getTopScores();
      if (sessionId === undefined || this.run?.sessionId === sessionId) {
        this.panel.renderLeaderboard(leaderboard);
        this.events.emit('leaderboard:updated', undefined);
      }
    } catch {
      if (sessionId === undefined || this.run?.sessionId === sessionId) {
        this.panel.renderLeaderboardError(() => {
          this.panel.setLeaderboardLoading();
          void this.loadLeaderboard(sessionId);
        });
      }
    }
  }

  private async submit(nickname: string): Promise<void> {
    const run = this.run;
    if (!run) return;
    this.panel.setSubmitting();
    const outcome = await this.submission.submit(
      createSubmitScorePayload(run, nickname, clientVersion),
    );
    if (!this.run || this.run.sessionId !== run.sessionId) return;
    if (outcome.status === 'success' && outcome.result) {
      this.storage.recordSuccessfulNickname(nickname);
    }
    this.panel.renderSubmission(outcome);
    if (outcome.status !== 'success' || !outcome.result) return;
    this.events.emit('leaderboard:score-saved', { score: outcome.result.score });
    this.panel.setLeaderboardLoading();
    try {
      this.panel.renderLeaderboard(await this.repository.getTopScores());
      this.events.emit('leaderboard:updated', undefined);
    } catch {
      this.panel.renderLeaderboardError(() => {
        this.panel.setLeaderboardLoading();
        void this.loadLeaderboard(run.sessionId);
      });
    }
  }
}
